import type { UseQueryResult } from '@tanstack/react-query';
import {
  type ColumnDef,
  type CreatedSortFn,
  createPaginatedRowModel,
  createSortedRowModel,
  type OnChangeFn,
  type ReactTable,
  type RowData,
  rowPaginationFeature,
  rowSortingFeature,
  type SortingState,
  tableFeatures,
} from '@tanstack/react-table';
import type { ParseKeys } from 'i18next';
import type { FieldFormat } from './memberViewFormat';
import type { DatasetKey } from './views';

/*
 * The table engine's types, bound once. Everything outside this file — the table surface, the
 * shell, each view's dataset entry — uses these aliases rather than TanStack's own, so the feature
 * set and the engine's generics stay in one place.
 */

/**
 * A column's share of the table, as a size the table maps to a width. Every column has one (`md`
 * when unset): with every column sized, the table hands out spare width by those sizes rather than
 * by whatever rows are on screen, so a sort or a page change can't move the columns.
 */
export type ColumnWidth = 'sm' | 'md' | 'lg' | 'xl';

/** What a Member Directory column carries besides TanStack's own definition. */
export interface MemberColumnMeta {
  width?: ColumnWidth;
}

/** The one feature set every Member Directory table runs on: client-side sorting and paging. */
export const memberTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
  columnMeta: {} as MemberColumnMeta,
});

export type MemberTableFeatures = typeof memberTableFeatures;
/** What a dataset's row may be — the bound a component generic over rows needs. */
export type MemberRowData = RowData;
export type MemberTable<TRow extends RowData> = ReactTable<MemberTableFeatures, TRow>;
export type MemberColumnDef<TRow extends RowData> = ColumnDef<MemberTableFeatures, TRow>;
/** A comparator usable on any dataset's rows. Return the ascending order; the table flips it for descending. */
export type MemberSortFn = CreatedSortFn<MemberTableFeatures, RowData>;
export type MemberSortingState = SortingState;
export type MemberSortUndefined = NonNullable<MemberColumnDef<RowData>['sortUndefined']>;
export type MemberOnSortingChange = OnChangeFn<SortingState>;

/** A key in the `providers` namespace — typed, so a mistyped label fails type-check. */
export type ProvidersKey = ParseKeys<'providers'>;

export interface MemberDatasetField<TRow> {
  id: string;
  label: ProvidersKey;
  value: (row: TRow) => unknown;
  format?: FieldFormat;
  /** Shown as a table column. */
  table?: boolean;
  /** The table column's size. Omitted means `md`. */
  width?: ColumnWidth;
  /**
   * Included in the CSV export, at `order`, under its own header — a report's headers often differ
   * from the table's (e.g. "Last AWP Date" against "Last AWP").
   */
  csv?: { order: number; label: ProvidersKey };
  searchable?: boolean;
  sortable?: boolean;
  /** Overrides the comparator the field's `format` would pick. */
  sortFn?: MemberSortFn;
  /**
   * Where a missing value sorts. Omitted means last in both directions. `-1` sorts it as the
   * smallest value: first ascending, last descending.
   */
  sortUndefined?: MemberSortUndefined;
}

/** A view's data, described as data: where its rows come from and what each field is. */
export interface MemberDataset<TRow> {
  /** Also the view's `?view=` value. */
  key: DatasetKey;
  /** A stable row identity — the member id, or an exam id for exam views. */
  rowKey: (row: TRow) => string;
  /** The operation's query hook, returning already-mapped domain rows. */
  useRows: () => UseQueryResult<TRow[]>;
  fields: MemberDatasetField<TRow>[];
  /** The name the A–Z bar groups this row under. */
  alphaKey?: (row: TRow) => string | null;
  /** Omitted means the server's order, with no column marked as sorted. */
  defaultSort?: MemberSortingState;
  /**
   * Every view loads its full row set and works on it in the browser. `ceiling` records the
   * row count past which a view would have to move to server-side paging; nothing enforces it yet.
   * Caching is the `useRows` hook's own concern.
   */
  loading: { mode: 'full'; ceiling: number };
  /** The report's file name. Required, so a new view can't download under a generic one. No patient data. */
  csvFileName: (ctx: { date: Date }) => string;
}
